/**
 * Os 25 pesos do CENA (mais 5 interruptores de bônus), em português.
 *
 * Um campo chamado `social_likes_p95_p99` não se explica sozinho, e quem
 * edita não é quem escreveu a fórmula. Cada peso tem rótulo e uma frase
 * dizendo o que ele faz — texto derivado do `CENA_ICENA_REFERENCE.md`,
 * que é o resumo em linguagem simples da fórmula.
 *
 * **A ordem daqui é a ordem da tela.** Agrupada por etapa do cálculo, e
 * não alfabética: a pessoa precisa ver "variáveis principais → sentimento
 * → bônus" na sequência em que a nota é montada, senão os campos viram
 * uma lista de números sem história.
 *
 * A paridade com o servidor é travada por teste
 * (`tests/test_cena_label_parity.py`, por FONTE): campo do payload
 * (`SCORE_CONFIG_FIELDS`/`SCORE_CONFIG_FLAGS` em `web_api/ports.py`) sem
 * rótulo aqui, ou rótulo aqui sem campo lá, falha — senão um peso novo
 * apareceria na tela como identificador cru (o que já aconteceu com as
 * tarefas de prompt) ou, pior, sumiria do formulário sem ninguém notar.
 * Correção de registro (2026-10-25, achado 28 da revisão de 2026-10-24):
 * este parágrafo afirmava o teste desde 2026-09-01 e ele não existia.
 */

export type CenaFieldGroup = "sentimento" | "imprensa" | "rede" | "escala";

export type CenaFieldLabel = {
  group: CenaFieldGroup;
  label: string;
  help: string;
};

export const CENA_GROUP_LABELS: Record<CenaFieldGroup, string> = {
  escala: "Escala da nota",
  sentimento: "Peso do sentimento",
  imprensa: "Imprensa",
  rede: "Redes sociais"
};

export const CENA_GROUP_HELP: Record<CenaFieldGroup, string> = {
  escala:
    "O limite do ESCena, que também é o divisor que transforma a soma em nota de 0 a 10.",
  sentimento:
    "Multiplica tudo o que veio antes. É o que faz a mesma exposição virar nota alta ou baixa conforme a menção seja positiva ou negativa.",
  imprensa:
    "Pesos e bônus das menções de imprensa: quanto vale o formato, o protagonismo da marca e a importância do veículo.",
  rede: "Pesos e bônus das menções de rede social, incluindo os pontos por faixa de engajamento."
};

export const CENA_FIELD_LABELS: Record<string, CenaFieldLabel> = {
  // ── escala ────────────────────────────────────────────────────────
  score_cap: {
    group: "escala",
    label: "Limite do ESCena",
    help: "O ESCena é limitado a mais ou menos este valor, e é ele que converte o resultado em nota de 0 a 10. Mexer aqui muda TODAS as notas de uma vez."
  },

  // ── sentimento ────────────────────────────────────────────────────
  fav_positive: {
    group: "sentimento",
    label: "Positivo",
    help: "Multiplicador aplicado quando a menção é positiva. Costuma ser o maior da escada."
  },
  fav_favorable: {
    group: "sentimento",
    label: "Favorável",
    help: "Multiplicador da menção favorável — entre o neutro e o positivo."
  },
  fav_unfavorable: {
    group: "sentimento",
    label: "Desfavorável",
    help: "Multiplicador da menção desfavorável. A fórmula espera um número negativo."
  },
  fav_negative: {
    group: "sentimento",
    label: "Negativo",
    help: "Multiplicador da menção negativa. A fórmula espera um número negativo, e costuma ser o mais forte da escada."
  },
  fav_neutral: {
    group: "sentimento",
    label: "Neutro",
    help: "Deixe vazio para que menções neutras fiquem SEM NOTA. Preencher com zero é diferente: a menção passa a ter nota, e ela cai perto de 5,0."
  },

  // ── imprensa ──────────────────────────────────────────────────────
  press_format_weight: {
    group: "imprensa",
    label: "Peso do formato",
    help: "Multiplica os pontos do formato da matéria (capa, nota, minutagem de TV ou rádio)."
  },
  press_approach_multiplier: {
    group: "imprensa",
    label: "Peso da abordagem",
    help: "Multiplica os pontos do protagonismo da marca na matéria: protagonista, coadjuvante ou citação."
  },
  press_vehicle_multiplier: {
    group: "imprensa",
    label: "Peso do veículo",
    help: "Multiplica os pontos do tier do veículo — o quanto aquele jornal ou emissora pesa."
  },
  press_complementary_multiplier: {
    group: "imprensa",
    label: "Peso dos bônus",
    help: "Multiplica a soma dos bônus (mensagem-chave, porta-voz, citação no título) antes de entrar no total."
  },
  press_key_message: {
    group: "imprensa",
    label: "Bônus de mensagem-chave",
    help: "Pontos somados quando a matéria carrega uma das mensagens-chave cadastradas."
  },
  press_spokesperson_mentioned: {
    group: "imprensa",
    label: "Bônus de porta-voz",
    help: "Pontos somados quando um porta-voz cadastrado aparece na matéria."
  },
  press_title_mention: {
    group: "imprensa",
    label: "Bônus de citação no título",
    help: "Pontos somados quando o título da matéria traz um dos termos cadastrados em Citação no título (Ajustes da empresa). Só impresso e online."
  },

  // ── rede ──────────────────────────────────────────────────────────
  social_interactions_weight: {
    group: "rede",
    label: "Peso das interações",
    help: "Multiplica os pontos vindos do engajamento do post (curtidas, comentários e o que mais a rede reportar)."
  },
  social_approach_multiplier: {
    group: "rede",
    label: "Peso da abordagem",
    help: "Multiplica os pontos do protagonismo da marca no post."
  },
  social_channel_multiplier: {
    group: "rede",
    label: "Peso do canal",
    help: "Multiplica o peso do canal (Instagram, TikTok…). O peso de cada canal é cadastrado por empresa, na aba Canais."
  },
  social_complementary_multiplier: {
    group: "rede",
    label: "Peso dos bônus",
    help: "Multiplica a soma dos bônus do post antes de entrar no total."
  },
  social_key_message: {
    group: "rede",
    label: "Bônus de mensagem-chave",
    help: "Pontos somados quando o post carrega uma das mensagens-chave cadastradas."
  },
  social_extra_point_p95: {
    group: "rede",
    label: "Bônus de post muito engajado",
    help: "Pontos extras para o post que passa do percentil 95 da própria marca no mês."
  },
  social_likes_p95: {
    group: "rede",
    label: "Curtidas até o p95",
    help: "Pontos quando as curtidas do post ficam ABAIXO do percentil 95 dos posts da mesma marca no mês."
  },
  social_likes_p95_p99: {
    group: "rede",
    label: "Curtidas entre p95 e p99",
    help: "Pontos quando as curtidas ficam entre o percentil 95 e o 99. Normalmente maior que a faixa anterior."
  },
  social_likes_p99: {
    group: "rede",
    label: "Curtidas acima do p99",
    help: "Pontos quando as curtidas passam do percentil 99 — o post excepcional do mês."
  },
  social_comments_p95: {
    group: "rede",
    label: "Comentários até o p95",
    help: "Mesma ideia das curtidas, contando comentários."
  },
  social_comments_p95_p99: {
    group: "rede",
    label: "Comentários entre p95 e p99",
    help: "Pontos quando os comentários ficam entre o percentil 95 e o 99."
  },
  social_comments_p99: {
    group: "rede",
    label: "Comentários acima do p99",
    help: "Pontos quando os comentários passam do percentil 99."
  }
};

/**
 * Os cinco bônus que a Administração pode DESLIGAR (2026-10-22): flag →
 * campo do bônus que ela governa. Desligado, o ponto conta zero nos
 * cálculos novos; o fato (mensagem-chave, porta-voz, citação) continua
 * gravado. As flags viajam no mesmo payload dos pesos (booleanos), mas
 * NÃO são pesos — por isso ficam fora de `CENA_FIELD_LABELS` e o
 * checkbox mora junto do campo do bônus.
 */
export const CENA_FLAG_FIELDS: Record<string, string> = {
  press_key_message_enabled: "press_key_message",
  press_spokesperson_enabled: "press_spokesperson_mentioned",
  press_title_mention_enabled: "press_title_mention",
  social_key_message_enabled: "social_key_message",
  social_extra_point_enabled: "social_extra_point_p95"
};

/** O inverso: campo do bônus → a flag que o liga e desliga. */
export const CENA_BONUS_FLAG: Record<string, string> = Object.fromEntries(
  Object.entries(CENA_FLAG_FIELDS).map(([flag, field]) => [field, flag])
);

/** A ordem em que os grupos aparecem: a sequência do cálculo. */
export const CENA_GROUP_ORDER: CenaFieldGroup[] = [
  "sentimento",
  "imprensa",
  "rede",
  "escala"
];

export function cenaFieldsOfGroup(group: CenaFieldGroup): string[] {
  return Object.entries(CENA_FIELD_LABELS)
    .filter(([, meta]) => meta.group === group)
    .map(([field]) => field);
}

/** O rótulo de uma tabela de ponto. */
export const CENA_POINT_TABLE_LABELS: Record<string, string> = {
  format: "Formato da matéria",
  approach: "Abordagem da marca",
  vehicle: "Tier do veículo",
  channel: "Canal de rede social"
};

/** O tipo de veiculação da tabela de formato (`format_score.broadcast_kind`);
 * o porte (P/M/G) já é a letra que a casa usa. */
export const CENA_BROADCAST_KIND_LABELS: Record<string, string> = {
  print_online: "Impresso e online",
  tv: "TV",
  radio: "Rádio"
};

/** O canal da tabela de pontos de rede (`channel_score.channel_name`). */
export const CENA_CHANNEL_LABELS: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X",
  youtube: "YouTube",
  tiktok: "TikTok"
};
